import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { propertiesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      if (isAdmin()) {
        const response = await propertiesAPI.getStats();
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Disponível': 'success',
      'Em Negociação': 'warning',
      'Reservado': 'info',
      'Locado': 'primary',
      'Indisponível': 'danger'
    };
    return colors[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Bem-vindo ao Sistema</h1>
            <p className="page-subtitle">Cadastro de Imóveis para ERBs - Telecomunicações</p>
          </div>
        </div>

        <div className="stats-grid">
          <Link to="/proprietarios/novo" className="stat-card" style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <div className="stat-icon primary">👤</div>
            <div className="stat-content">
              <div className="stat-value">Novo</div>
              <div className="stat-label">Cadastrar Proprietário</div>
            </div>
          </Link>
          
          <Link to="/imoveis/novo" className="stat-card" style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <div className="stat-icon success">🏢</div>
            <div className="stat-content">
              <div className="stat-value">Novo</div>
              <div className="stat-label">Cadastrar Imóvel</div>
            </div>
          </Link>
          
          <Link to="/mapa" className="stat-card" style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <div className="stat-icon info">🗺️</div>
            <div className="stat-content">
              <div className="stat-value">Ver</div>
              <div className="stat-label">Mapa de Imóveis</div>
            </div>
          </Link>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📋 Instruções</h3>
          </div>
          <div className="card-body">
            <p style={{ marginBottom: '16px' }}>
              Este sistema permite o cadastro de imóveis para futura locação de espaços para 
              instalação de Estações Rádio Base (ERBs) de telecomunicações.
            </p>
            <h4 style={{ marginBottom: '12px', color: 'var(--gray-700)' }}>Como cadastrar:</h4>
            <ol style={{ paddingLeft: '20px', color: 'var(--gray-600)' }}>
              <li style={{ marginBottom: '8px' }}>Primeiro, cadastre o proprietário do imóvel com todos os dados pessoais e de contato</li>
              <li style={{ marginBottom: '8px' }}>Em seguida, cadastre o imóvel vinculando ao proprietário</li>
              <li style={{ marginBottom: '8px' }}>Marque no mapa o ponto central da área disponível para locação</li>
              <li style={{ marginBottom: '8px' }}>Selecione os documentos disponíveis e a área oferecida</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Visão geral do sistema de cadastro de imóveis</p>
        </div>
        <div className="btn-group">
          <Link to="/proprietarios/novo" className="btn btn-secondary">
            👤 Novo Proprietário
          </Link>
          <Link to="/imoveis/novo" className="btn btn-primary">
            🏢 Novo Imóvel
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon primary">🏢</div>
          <div className="stat-content">
            <div className="stat-value">{stats?.totalProperties || 0}</div>
            <div className="stat-label">Total de Imóveis</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon success">👤</div>
          <div className="stat-content">
            <div className="stat-value">{stats?.totalOwners || 0}</div>
            <div className="stat-label">Proprietários</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon info">✅</div>
          <div className="stat-content">
            <div className="stat-value">
              {stats?.byAvailability?.find(b => b._id === 'Disponível')?.count || 0}
            </div>
            <div className="stat-label">Disponíveis</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon warning">🤝</div>
          <div className="stat-content">
            <div className="stat-value">
              {stats?.byAvailability?.find(b => b._id === 'Em Negociação')?.count || 0}
            </div>
            <div className="stat-label">Em Negociação</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
        {/* Por Status de Disponibilidade */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📊 Por Disponibilidade</h3>
          </div>
          <div className="card-body">
            {stats?.byAvailability?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {stats.byAvailability.map((item) => (
                  <div key={item._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span className={`badge badge-${getStatusColor(item._id)}`}>{item._id || 'Não definido'}</span>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-state-text">Nenhum dado disponível</p>
            )}
          </div>
        </div>

        {/* Por Tipo de Imóvel */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">🏠 Por Tipo de Imóvel</h3>
          </div>
          <div className="card-body">
            {stats?.byPropertyType?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {stats.byPropertyType.map((item) => (
                  <div key={item._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{item._id || 'Não definido'}</span>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-state-text">Nenhum dado disponível</p>
            )}
          </div>
        </div>

        {/* Por Área Disponível */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📐 Por Área Disponível</h3>
          </div>
          <div className="card-body">
            {stats?.byArea?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {stats.byArea.map((item) => (
                  <div key={item._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{item._id || 'Não definido'}</span>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-state-text">Nenhum dado disponível</p>
            )}
          </div>
        </div>

        {/* Por Estado */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📍 Por Estado</h3>
          </div>
          <div className="card-body">
            {stats?.byState?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {stats.byState.map((item) => (
                  <div key={item._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{item._id || 'Não definido'}</span>
                    <strong>{item.count}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-state-text">Nenhum dado disponível</p>
            )}
          </div>
        </div>
      </div>

      {/* Últimos Cadastros */}
      <div className="card" style={{ marginTop: '20px' }}>
        <div className="card-header">
          <h3 className="card-title">🕐 Últimos Cadastros</h3>
          <Link to="/imoveis" className="btn btn-sm btn-outline">Ver todos</Link>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {stats?.recentProperties?.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Identificação</th>
                    <th>Localização</th>
                    <th>Status</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentProperties.map((property) => (
                    <tr key={property._id}>
                      <td>
                        <Link to={`/imoveis/${property._id}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                          {property.propertyName || 'Sem nome'}
                        </Link>
                      </td>
                      <td>{property.address?.city}/{property.address?.state}</td>
                      <td>
                        <span className={`badge badge-${getStatusColor(property.availabilityStatus)}`}>
                          {property.availabilityStatus}
                        </span>
                      </td>
                      <td>{new Date(property.createdAt).toLocaleDateString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <div className="empty-state-title">Nenhum imóvel cadastrado</div>
              <div className="empty-state-text">
                Comece cadastrando proprietários e imóveis para visualizar os dados aqui.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
